/**
 * Per-node-type output declarations.
 *
 * Single source of truth for "what variables does a node of type X produce?"
 * Consumed by:
 *   - extractDefinedVariablesFromConfig (validation, both web + api)
 *   - useWorkflowVariables (web variable picker)
 *
 * Add a new node type's outputs here; all consumers pick them up automatically.
 */

import type { VariableSource } from './macros.js';

export interface NodeOutput {
  name: string;
  designTimeValue?: string;
  source: VariableSource;
}

export type NodeOutputResolver = (config: Record<string, unknown>) => NodeOutput[];

export const NODE_OUTPUT_RESOLVERS: Record<string, NodeOutputResolver> = {
  sandbox_action: (cfg) => {
    const keys = Array.isArray(cfg.outputKeys) ? cfg.outputKeys : [];
    return keys
      .filter((k): k is string => typeof k === 'string' && k.length > 0)
      .map((name) => ({ name, source: 'extracted' as const }));
  },
  mcp: (cfg) => {
    const keys = Array.isArray(cfg.declaredOutputs) ? cfg.declaredOutputs : [];
    return keys
      .filter((k): k is string => typeof k === 'string' && k.length > 0)
      .map((name) => ({ name, source: 'extracted' as const }));
  },
  notification: (cfg) => {
    const out: NodeOutput[] = [];
    for (const field of ['to', 'subject', 'body'] as const) {
      const v = cfg[field];
      if (typeof v === 'string' && v.length > 0) {
        out.push({
          name: `notification.${field}`,
          designTimeValue: v,
          source: 'inputValue',
        });
      }
    }
    return out;
  },
};

export function resolveNodeOutputs(
  nodeType: string | undefined,
  config: Record<string, unknown>
): NodeOutput[] {
  if (!nodeType) return [];
  const resolver = NODE_OUTPUT_RESOLVERS[nodeType];
  return resolver ? resolver(config) : [];
}
